import { BucklingStatus, ParameterGroup } from "../types/ApiData";
import L from 'leaflet';
import { greenIcon, yellowIcon, orangeIcon, redIcon, activeGreenIcon, activeYellowIcon, activeOrangeIcon, activeRedIcon, greyIcon, activeGreyIcon } from '../components/MarkerIcon';
import { OnlineNetworkItems } from "../state/application-state";


export interface Condition {
    field: string;
    op: ">" | "<" | "==" | "!=" | ">=" | "<=" | "between";
    value: any;
}

interface ValidationRule {
  when?: Condition[];
  whenAny?: Condition[];

  min?: number;
  max?: number;


  op?: ">" | "<" | ">=" | "<=" | "==" | "!=";
  value?: any;
  message: string;
  type?: number
}

export interface SetValueRule {
  when?: Condition[];
  whenAny?: Condition[];
  value: any;
}

export interface DynamicRules {
    visible?: { when?: Condition[]; whenAny?: Condition[] }[];
    required?: { when?: Condition[]; whenAny?: Condition[] }[];
    validation?: ValidationRule[];
    defaultValue?: any;
    disabled?: { when?: Condition[]; whenAny?: Condition[] }[];
    range?: { when?: Condition[]; min?: number; max?: number }[];
    set_value?: SetValueRule[];
}

export type FormValues = Record<string, any>;

export function evaluateCondition(cond: Condition, formValues: FormValues) {
    const val = formValues[cond.field];
    switch (cond.op) {
        case ">": return val > cond.value;
        case "<": return val < cond.value;
        case ">=": return val >= cond.value;
        case "<=": return val <= cond.value;
        case "==": return isEqual(val, cond.value);
        case "!=": return !isEqual(val, cond.value);
        case "between": return Array.isArray(cond.value) && val >= cond.value[0] && val <= cond.value[1];
        default: return false;
    }
}

function isEqual(a: unknown, b: unknown): boolean {
  // If both are numbers (or numeric strings), compare as numbers
  const aNum = Number(a);
  const bNum = Number(b);

  if (!isNaN(aNum) && !isNaN(bNum)) {
    return aNum === bNum;
  }

  // Otherwise compare as strings
  return String(a) === String(b);
}

// Evaluate 'when' or 'whenAny' rules
export function evaluateWhen(rules: { when?: Condition[]; whenAny?: Condition[] }[], formValues: FormValues) {
  const evaluate = rules.some(rule => {
        if (rule.when) {
            return rule.when.every(cond => evaluateCondition(cond, formValues))
        };
        if (rule.whenAny) {

            return rule.whenAny.some(cond => evaluateCondition(cond, formValues));
        }
        return true;
    });
    return evaluate
}

export type ValidationMessage = {
  message: string;
  type: number
};

export function validateField(
  fieldCode: string,
  value: any,
  formValues: FormValues,
  rules: Record<string, DynamicRules>
): ValidationMessage | null {
  if(typeof rules === 'object' && Object.keys(rules).length === 0) return null;
  const fieldRules = rules[fieldCode];
  if (!fieldRules?.validation) return null;

  let bestResult: ValidationMessage | null = null;

  for (const rule of fieldRules.validation) {
    const active =
      rule.when
        ? rule.when.every(c => evaluateCondition(c, formValues))
        : rule.whenAny
        ? rule.whenAny.some(c => evaluateCondition(c, formValues))
        : true;

    if (active && rule.type === 1) {
      return {
        message: rule.message,
        type: rule.type ?? 1 // default to error
      };
    } else if(active && rule.type === 2) {
        bestResult = { message: rule.message, type: 2 };
    }

    if (!active) continue;
    // min / max
    /*if (typeof rule.min === "number" && value < rule.min) return { message: rule.message, type: rule.type ?? 1} 
    if (typeof rule.max === "number" && value > rule.max) return { message: rule.message, type: rule.type ?? 1} */

    // explicit operator (self validation)
    if (rule.op && rule.value !== undefined) {
      switch (rule.op) {
        case "<": if ((value < rule.value)) return { message: rule.message, type: rule.type ?? 1}; break;
        case ">": if ((value > rule.value)) return  { message: rule.message, type: rule.type ?? 1}; break;
        case "<=": if ((value <= rule.value)) return  { message: rule.message, type: rule.type ?? 1}; break;
        case ">=": if ((value >= rule.value)) return  { message: rule.message, type: rule.type ?? 1}; break;
        case "==": if ((value === rule.value)) return  { message: rule.message, type: rule.type ?? 1}; break;
        case "!=": if ((value !== rule.value)) return  { message: rule.message, type: rule.type ?? 1}; break;
      }
    }
  }

  return bestResult;
}

export function validateAllFields(
  values: FormValues,
  rules: Record<string, DynamicRules>
): Record<string, ValidationMessage | undefined> {
  const errors: Record<string, ValidationMessage | undefined> = {};

  Object.keys(values).forEach((field) => {
    const result = validateField(field, values[field], values, rules);

    if (result) {
      errors[field] = result;
    }
  });

  return errors;
}


export function applySetValueRules(
  changedField: string,
  values: Record<string, any>,
  rulesMap: Record<string, DynamicRules>,
  setValueGraph: Record<string, string[]>
) {
  let nextValues = { ...values };

  setValueGraph[changedField]?.forEach(targetField => {
    const rules = rulesMap[targetField]?.set_value;
    if (!rules) return;

    rules.forEach(rule => {
      const match =
        rule.when
          ? rule.when.every(c => evaluateCondition(c, nextValues))
          : rule.whenAny
          ? rule.whenAny.some(c => evaluateCondition(c, nextValues))
          : false;

      if (match && nextValues[targetField] !== rule.value) {
        nextValues[targetField] = rule.value;
      }
    });
  });

  return nextValues;
}

export function buildSetValueDependencyGraph(
  groups: ParameterGroup[]
): Record<string, string[]> {
  const graph: Record<string, Set<string>> = {};

  groups.forEach(group => {
    group.parameters.forEach(param => {
      const setRules = param.relations?.set_value;
      if (!setRules) return;

      setRules.forEach(rule => {
        [...(rule.when ?? []), ...(rule.whenAny ?? [])].forEach(cond => {
          if (cond.field === param.code) return;

          if (!graph[cond.field]) {
            graph[cond.field] = new Set();
          }
          graph[cond.field].add(param.code);
        });
      });
    });
  });

  return Object.fromEntries(
    Object.entries(graph).map(([k, v]) => [k, Array.from(v)])
  );
}

export function buildDependencyGraphs(
  groups: ParameterGroup[]
): Record<string, string[]> {
  const graph: Record<string, Set<string>> = {};

  groups.forEach(group => {
    group.parameters.forEach(param => {
      const validations = param.relations?.validation ?? '';
      if (!validations) return;

      validations?.forEach(rule => {
        [...(rule.when ?? []), ...(rule.whenAny ?? [])].forEach(cond => {
          if (cond.field === param.code) return; 

          if (!graph[cond.field]) {
            graph[cond.field] = new Set();
          }
          graph[cond.field].add(param.code);
        });
      });
    });
  });

  return Object.fromEntries(
    Object.entries(graph).map(([k, v]) => [k, Array.from(v)])
  );
}

export function buildRulesMap(
  groups: ParameterGroup[]
): Record<string, Partial<DynamicRules>> {
  const rulesMap: Record<string, Partial<DynamicRules>> = {};
  groups.forEach(group => {
    group.parameters.forEach(param => {
      rulesMap[param.code] = param.relations ?? {};
    });
  });
  return rulesMap;
}

type BucklingLevel = 'light' | 'dark'

export function getBucklingColor(
  buckling_status: string,
  schema: BucklingStatus[],
  level: BucklingLevel
): string {
  if (!buckling_status) return level === 'dark' ? 'green' : '#72f88f'
  let color = 'green'

  if(schema && schema.length > 0) {
    const item = schema.find(item => item.code === buckling_status)
    color = ( level === 'dark' ? item?.color_code_dark : item?.color_code_light) ?? '#72f88f'
  }
  return color
}

export const createTaggedIcon = (
  iconUrl: string,
  label: string
) =>
  L.divIcon({
    className: "bg-transparent",
    html: `
      <div class="relative flex flex-col items-center">
        
        <div class="bg-gray-700 text-white text-[10px] px-2 py-0.5 rounded-full shadow -mb-1 z-10">
          ${label}
        </div>

        <img
          src="${iconUrl}"
          style="width:25px;height:41px;"
        />
      </div>
    `,
    iconSize: [25, 41],
    iconAnchor: [13, 41],
    popupAnchor: [0, -40],
  });

export const getStatuColor = (buckling_status : string, schema: BucklingStatus[]) => {
  let statusInfo = { status: 'undefined', badgeColor: 'secondary', textColor: 'white', state: 'g1', icon: '' }

  if(schema && schema.length > 0) {
    const item = schema.find(item => item.code === buckling_status)
    statusInfo.status = item?.label ?? 'undefined'
    statusInfo.badgeColor = item?.color_code_dark ?? 'secondary'
    statusInfo.state = item?.code ?? 'g1'
    statusInfo.icon = item?.icon_code ?? 'poi-green-active'
  }
  return statusInfo
}

export const getMarker = (state: string) => {
  let iconInfo = { icon: greyIcon, activeIcon: activeGreyIcon }
  switch (state) {
    case 'poi-green-active':
      iconInfo.icon = greenIcon
      iconInfo.activeIcon = activeGreenIcon
      break;
    case 'poi-yellow-active':
      iconInfo.icon = yellowIcon
      iconInfo.activeIcon = activeYellowIcon
      break
    case 'poi-orange-active':
      iconInfo.icon = orangeIcon
      iconInfo.activeIcon = activeOrangeIcon
      break;
    case 'poi-red-active':
      iconInfo.icon = redIcon
      iconInfo.activeIcon = activeRedIcon
      break;
    default:
      iconInfo.icon = greyIcon
      iconInfo.activeIcon = activeGreyIcon
      break;
  }
  return iconInfo
}

export const buildDefaultsFromSchema = (
  storedInputParams: ParameterGroup[]
): FormValues => {
  const defaults: FormValues = {};

  storedInputParams.forEach(group => {
    group.parameters.forEach(param => {
      defaults[param.code] =
        (param.type === 1) && param.default != null
          ? (param.default)
          : param.default;
    });
  });

  return defaults;
};

export const buildResultFromParams = (
  storedInputParams: ParameterGroup[],
  item: Record<string, any>
) => {
  const result: {
    key: string;
    label: string;
    unit?: string;
    type?: number;
    options?: { label: string; value: string }[];
    displayValue: any;
    hint: string;
  }[] = [];

  storedInputParams.forEach((group) => {
    group.parameters.forEach((param) => {
      if (!(param.code in item)) return;

      let displayValue = item[param.code];

      if (param.type === 2 && param.options) {
        const option = param.options.find((opt) => opt.value === displayValue);
        if (option) displayValue = option.label;
      }

      result.push({
        key: param.code,
        label: param.label ?? param.code,
        unit: param.unit ?? "",
        type: param.type,
        options: param.options,
        displayValue,
        hint: param.hint ?? "",
      });
    });
  });

  return result;
};


export const formatDateToYMD = (date: Date): string => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const triggerFileDownload = (fileUrl: string, fileName?: string) => {
  const link = document.createElement("a");
  link.href = fileUrl;
  link.target = "_blank";
  link.rel = "noopener noreferrer";

  if (fileName) {
    link.download = fileName;
  }

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const getNetworkName = (id: string): string => {
  const network: Record<string, string> = {
    "c878cb04-5f21-4d4e-895f-1f7d6c1b24af": "hamburg-kiel",
    "c836c4d1-d36f-4f1f-b957-cbf884b9e1b7": "rail-center-hamburg",
    "5169ee7f-d39b-4b9c-bd31-c382f716f688": "benidorm-denia",
    "2d26167a-9bdd-4965-81e6-f5cc33c310e4": "hora-malesov",
    "d347c8f8-1b8e-492a-991c-dc735f63137c": "usti-brandys",
  };

  return network[id] ?? "unknown-network";
};

export const getNetworkNameForOnlineMaps = (id: string, onlineNetworks: OnlineNetworkItems[]): string => {
  const item = onlineNetworks.find((item) => item.network_uuid === id)

  return item?.name ?? "unknown-network";
};

type ApiResponse = Record<string, any>;

export function extractSchemaValues(
  schema: ParameterGroup[],
  apiResponse: ApiResponse
) {
  // 1. Collect all parameter codes
  const codes = new Set<string>();

  schema.forEach(group => {
    group.parameters.forEach(param => {
      codes.add(param.code);
    });
  });

  // 2. Build result object
  const result: Record<string, any> = {};

  // Always include id
  if (apiResponse.id !== undefined) {
    result.id = apiResponse.id;
  }

  // 3. Filter only matching codes
  codes.forEach(code => {
    if (code in apiResponse) {
      result[code] = apiResponse[code];
    }
  });

  return result;
}


export const normalizeBooleans = (obj: any): any => {
  if (obj === null || obj === undefined) return obj;

  if (typeof obj === "boolean") {
    return obj ? "1" : "0";
  }

  if (Array.isArray(obj)) {
    return obj.map(normalizeBooleans);
  }

  if (typeof obj === "object") {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        key,
        normalizeBooleans(value),
      ])
    );
  }

  return obj;
};